import toast from 'react-hot-toast';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
	createBulkConsignmentOrder,
	getBulkConsignmentOrders,
	getConsignmentOrderById,
	getConsignmentOrderShipments,
	getConsignmentOrders,
	getConsignmentProducts,
	updateBulkConsignmentOrder,
} from '@/services/consignmentOrders';
import type { BulkOrderRequest } from '@/types/bulk-order';
import type { FilterType } from '@/types/table';

export const useGetConsignmentOrders = ({
	page = 1,
	limit = 15,
	enabled = true,
	columnsFilters = '[]',
	search = '',
	status = '',
	globalFilters = '[]',
	sort = '',
}: FilterType) => {
	return useQuery({
		queryKey: [
			'consignmentOrders',
			page,
			limit,
			columnsFilters,
			search,
			status,
			globalFilters,
			sort,
		],
		queryFn: () =>
			getConsignmentOrders(
				page,
				limit,
				columnsFilters,
				search,
				status as string | number,
				globalFilters as string,
				sort as string,
			),
		enabled,
		keepPreviousData: false,
	});
};

// Hook to get request products for consignment orders
export const useGetConsignmentProducts = ({
	page = 1,
	limit = 15,
	enabled = true,
	globalFilters = '[]',
}: FilterType) => {
	return useQuery({
		queryKey: ['consignment-products', page, limit, globalFilters],
		queryFn: () => getConsignmentProducts(page, limit, globalFilters as string),
		enabled,
		keepPreviousData: false,
	});
};

/**
 * Hook to get single consignment order by ID
 */
export const useGetConsignmentOrderById = (
	orderId: string | null,
	enabled: boolean,
) => {
	return useQuery({
		queryKey: ['consignmentOrder', orderId],
		queryFn: () => getConsignmentOrderById(orderId as string),
		enabled: enabled && !!orderId,
		keepPreviousData: false,
	});
};

export const useGetConsignmentByIdMutation = (
	onSuccess?: (data: any) => void,
) => {
	return useMutation({
		mutationFn: (orderId: string) => getConsignmentOrderById(orderId),
		onSuccess: (data) => {
			onSuccess?.(data);
		},
		onError: (error: unknown) => {
			toast.error(
				(error as any)?.response?.data?.error?.message?.message ||
					'Failed to fetch consignment order details. Please try again.',
			);
		},
	});
};

/**
 * Hook to get consignment order shipment details
 */
export const useGetConsignmentOrderShipments = (
	orderId: string | null,
	enabled: boolean,
) => {
	return useQuery({
		queryKey: ['consignmentOrderShipments', orderId],
		queryFn: () => getConsignmentOrderShipments(orderId as string),
		enabled: enabled && !!orderId,
		keepPreviousData: false,
	});
};

/**
 * Hook to get bulk consignment order group for edit/reorder
 */
export const useGetBulkConsignmentOrders = (
	orderId: string | null,
	enabled: boolean,
) => {
	return useQuery({
		queryKey: ['bulkConsignmentOrders', orderId],
		queryFn: () => getBulkConsignmentOrders(orderId as string),
		enabled: enabled && !!orderId,
		keepPreviousData: false,
	});
};

/**
 * Hook to create bulk consignment order
 * Sends ONE request to backend which creates multiple consignment orders
 */
export const useCreateBulkConsignmentOrder = (
	onSuccess?: (data: any) => void,
) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (payload: BulkOrderRequest & { ta_consignmenttype: number }) =>
			createBulkConsignmentOrder(payload),
		onSuccess: (data) => {
			queryClient.invalidateQueries(['consignmentOrders']);
			if (onSuccess) onSuccess(data);
		},
	});
};

/**
 * Hook to update bulk consignment order
 */
export const useUpdateBulkConsignmentOrder = (
	onSuccess?: (data: any) => void,
) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (payload: any) => updateBulkConsignmentOrder(payload),
		onSuccess: (data) => {
			queryClient.invalidateQueries(['consignmentOrders']);
			if (onSuccess) onSuccess(data);
		},
	});
};
